import { responseError } from '@/utils/errors';
import findResult, {
  findWithCode,
  assignIfExists,
  mapResults,
  findWithReadStatus,
  removeItemByCode,
  assignValue,
  assignProps,
  selectableResult,
  findSelectedInCategory,
} from '@/utils/resultsHelper';
import withDraft from '@/utils/draftHelper';
import { states, updateStates } from '@/constants/lifecycle';
import updateMultiplePanels from '@/utils/panels';
import actions, {
  roleLists,
  roleLifeCycle,
  roleGroupings,
} from '../constants';
import { permissionsMetrics, smartToggleRolePermission, smartUpdateRolePermissions } from '../helper';
import initialState, {
  activeRole,
  pruneRoleErrors,
  newRoleProperties,
  rolePanels,
  rolesReassign,
  defaultRole,
  rolesToSetAsDefault,
  roleStateToLoad, findRoles, findRole,
} from './state';
import { roleActionsState } from '../roleactions/state/state';

const toggleRolesSelected = withDraft((draft, action) => {
  const item = findRoles(draft, action);
  item.results.forEach((result) => {
    assignValue(result, 'selected', action.selected);
  });
});

const updateRolesFilter = withDraft((draft, action) => {
  const item = findRoles(draft, action);
  item.filter = action.value;
});

const fetchRoles = withDraft((draft, action) => {
  const item = findRoles(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const showRoles = withDraft((draft, action) => {
  const item = findRoles(draft, action);
  const results = action.results.map((roleItem) => ({ ...roleItem, roleActions: roleActionsState }));
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, { results }, selectableResult)
    : mapResults([], { results }, selectableResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showRolesError = withDraft((draft, action) => {
  const item = findRoles(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeRoles = withDraft((draft, action) => {
  draft.roles[action.category] = roleStateToLoad({ roles: draft }).roles[action.category];
});

const updateRolesFlags = withDraft((draft, action) => {
  const item = findRoles(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.userFlag = action.userFlag;
  item.tokenFlag = action.tokenFlag;
  item.assignable = action.assignable;
  item.currentPage = 1;
});

const toggleRolesPresentation = withDraft((draft, action) => {
  const item = findRoles(draft, action);
  item.display = action.display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

const updateRolePermissions = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.permissions = smartUpdateRolePermissions(item, action.permissionCategories, action.status);
  item.isEditing = true;
  item.isDirty = true;
});

const toggleRolePermission = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.permissions = smartToggleRolePermission(item, action.roleCategory, action.roleAction);
  item.isEditing = true;
  item.isDirty = true;
});

const updateRoleFilter = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.filter = action.value;
  if (action.value) {
    item.panels = rolePanels([0, 1, 2, 3]).panels;
  }
});

const updatePermissionsPanels = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.panels[action.panel] = updateMultiplePanels(item, action);
});

const updateRoleProperties = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.isEditing = true;
  assignIfExists(item, action.properties, roleLifeCycle, true);
});

const showSaveRole = withDraft((draft, action) => {
  const item = findRole(draft, action);
  assignIfExists(draft.activeRole, newRoleProperties(item, action));
  if (item) {
    draft.roles.lookups.userFlag = (item.grouping === roleGroupings.USERS);
    draft.roles.lookups.tokenFlag = (item.grouping === roleGroupings.TOKENS);
  }
});

const showConfirmPermissions = withDraft((draft, action) => {
  draft.activeRole.confirmChangesOpen = true;
  draft.activeRole.code = action.code;
  draft.activeRole.category = action.category;
});

const editRole = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.isEditing = true;
});

const updateRole = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.updateStatus = states.PENDING;
  item.updateErrors = [];
});

const toggleRoleSelected = withDraft((draft, action) => {
  const item = findRole(draft, action);
  if (!item.selected && !draft.roles[action.category].multiSelect) {
    const selectedRoles = findSelectedInCategory(draft.roles, action.category);
    selectedRoles.forEach((role) => assignValue(role, 'selected', false));
  }
  item.selected = !item.selected;
});

const fetchRole = withDraft((draft, action) => {
  const item = findRole(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    draft.roles[action.category].results.push({
      code: action.code,
      readStatus: states.PENDING,
      readErrors: [],
      ...rolePanels(),
    });
  }
});

const showRole = withDraft((draft, action) => {
  const item = findRole(draft, action);
  assignProps(item, {
    ...action.result,
    readStatus: states.SUCCESS,
    readErrors: [],
    updateStatus: states.NONE,
    updateErrors: [],
    roleActions: { ...roleActionsState },
    ...updateStates,
    copy: {
      ...action.result,
    },
    isEditing: false,
    isDirty: false,
    filter: '',
    smartFlag: action.result.grouping === roleGroupings.USERS,
    panels: {
      single: [0],
      left: [0],
      right: [0],
    },
  });
});

const showRoleError = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeRole = withDraft((draft, action) => {
  const item = findRole(draft, action);
  if (item) {
    draft.roles[action.category].results = removeItemByCode(draft.roles[action.category].results, item);
  }
});

const editRoleCancel = withDraft((draft, action) => {
  const item = findRole(draft, action);
  item.isEditing = false;
  item.updateStatus = states.NONE;
  item.updateErrors = [];
  item.isDirty = false;
});

const editRoleSuccess = withDraft((draft, action) => {
  const item = findRole(draft, action);
  draft.activeRole = activeRole();
  assignIfExists(item, {
    ...action.result,
    isDirty: false,
    isEditing: false,
    copy: {
      ...action.result,
    },
    ...updateStates,
  });
  const categories = findWithCode(draft.roles, roleLists, action.code);
  categories.forEach((category) => {
    const role = findResult(draft.roles, category, action.code);
    assignIfExists(role, {
      ...action.result,
      metrics: permissionsMetrics(action.result),
    });
  });
});

const editRoleError = withDraft((draft, action) => {
  const item = findRole(draft, action);
  draft.activeRole = activeRole();
  assignIfExists(item, {
    updateStatus: states.ERROR,
    updateErrors: [responseError(action)],
  });
});

const updateUsersRoleErrorOrSuccess = withDraft((draft) => {
  draft.roles.reassign = rolesReassign();
});

const updateUsersRole = withDraft((draft) => {
  draft.roles.reassign.updateStatus = states.PENDING;
});

const showRoleReassign = withDraft((draft, action) => {
  draft.roles.reassign.open = true;
  draft.roles.reassign.users = action.users;
});

const newRole = withDraft((draft) => {
  assignIfExists(
    draft.activeRole,
    { ...activeRole(), createStatus: states.SUCCESS },
  );
  findWithReadStatus(draft.roles, roleLists, states.SUCCESS).forEach((category) => {
    draft.roles[category].readStatus = states.PENDING;
    draft.roles[category].readErrors = [];
  });
});

const hideActiveRole = withDraft((draft) => {
  draft.roles.lookups = initialState.roles.lookups;
  draft.activeRole = activeRole();
});

const createRole = withDraft((draft) => {
  draft.activeRole.createStatus = states.PENDING;
});

const updateActiveRoleProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeRole, action.properties);
  draft.activeRole.createErrors = pruneRoleErrors(draft.activeRole.createErrors, action);
});

const newRoleError = withDraft((draft, action) => {
  assignIfExists(draft.activeRole, {
    createStatus: states.ERROR,
    createErrors: [responseError(action)],
  });
});

const showDefaultRole = withDraft((draft) => {
  draft.roles.defaultSelection.open = true;
});

const hideUsersRoleReassign = withDraft((draft) => {
  draft.roles.reassign = rolesReassign();
});

const hideDefaultRole = withDraft((draft) => {
  draft.roles.defaultSelection = rolesToSetAsDefault();
});

const fetchDefaultRole = withDraft((draft) => {
  draft.roles.defaultRole.results[0] = defaultRole(states.PENDING);
});

const applyDefaultRoleCode = withDraft((draft, action) => {
  draft.roles.defaultRole.results[0].code = action.code;
});

export default {
  [actions.APPLY_DEFAULT_ROLE_CODE]: applyDefaultRoleCode,
  [actions.CLOSE_ROLE]: closeRole,
  [actions.CLOSE_ROLES]: closeRoles,
  [actions.CREATE_ROLE]: createRole,
  [actions.EDIT_ROLE]: editRole,
  [actions.EDIT_ROLE_CANCEL]: editRoleCancel,
  [actions.EDIT_ROLE_ERROR]: editRoleError,
  [actions.EDIT_ROLE_SUCCESS]: editRoleSuccess,
  [actions.FETCH_DEFAULT_ROLE]: fetchDefaultRole,
  [actions.FETCH_ROLE]: fetchRole,
  [actions.FETCH_ROLES]: fetchRoles,
  [actions.HIDE_CONFIRM_PERMISSIONS]: hideActiveRole,
  [actions.HIDE_DEFAULT_ROLE]: hideDefaultRole,
  [actions.HIDE_SAVE_ROLE]: hideActiveRole,
  [actions.HIDE_USERS_ROLE_REASSIGN]: hideUsersRoleReassign,
  [actions.NEW_ROLE]: newRole,
  [actions.NEW_ROLE_ERROR]: newRoleError,
  [actions.REFRESH_ROLES]: fetchRoles,
  [actions.SHOW_CONFIRM_PERMISSIONS]: showConfirmPermissions,
  [actions.SHOW_DEFAULT_ROLE]: showDefaultRole,
  [actions.SHOW_ROLE_REASSIGN]: showRoleReassign,
  [actions.SHOW_ROLE]: showRole,
  [actions.SHOW_ROLE_ERROR]: showRoleError,
  [actions.SHOW_ROLES]: showRoles,
  [actions.SHOW_ROLES_ERROR]: showRolesError,
  [actions.SHOW_SAVE_ROLE]: showSaveRole,
  [actions.TOGGLE_ROLE_PERMISSION]: toggleRolePermission,
  [actions.TOGGLE_ROLE_SELECTED]: toggleRoleSelected,
  [actions.TOGGLE_ROLES_PRESENTATION]: toggleRolesPresentation,
  [actions.TOGGLE_ROLES_SELECTED]: toggleRolesSelected,
  [actions.UPDATE_ACTIVE_ROLE_PROPERTIES]: updateActiveRoleProperties,
  [actions.UPDATE_PERMISSIONS_PANELS]: updatePermissionsPanels,
  [actions.UPDATE_ROLE]: updateRole,
  [actions.UPDATE_ROLE_FILTER]: updateRoleFilter,
  [actions.UPDATE_ROLE_PERMISSIONS]: updateRolePermissions,
  [actions.UPDATE_ROLE_PROPERTIES]: updateRoleProperties,
  [actions.UPDATE_ROLES_FILTER]: updateRolesFilter,
  [actions.UPDATE_ROLES_FLAGS]: updateRolesFlags,
  [actions.UPDATE_USERS_ROLE]: updateUsersRole,
  [actions.UPDATE_USERS_ROLE_ERROR]: updateUsersRoleErrorOrSuccess,
  [actions.UPDATE_USERS_ROLE_SUCCESS]: updateUsersRoleErrorOrSuccess,
};
